import { useState } from "react";
import { Pencil, Trash2, AlertCircle, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { SignedAudio } from "@/lib/media";

function Paragraph({ p, sources, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [text, setText] = useState(p.text);
  const [show, setShow] = useState(false);
  const save = async (body) => { try { onSaved((await api.put(`/story2/draft/paragraphs/${p.id}`, body)).data); setEdit(false); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="mb-4" data-testid={`para-${p.id}`}>
      {edit ? (
        <>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} data-testid={`para-input-${p.id}`}
            className="w-full rounded-lg bg-[#1A1222] border border-[#3B2D52] p-3 text-[#EDE6F7] text-lg leading-relaxed" />
          <div className="flex gap-2 mt-2">
            <button onClick={() => save({ text })} data-testid={`para-save-${p.id}`} className="gold-btn h-10 px-5 rounded-full">Save correction</button>
            <button onClick={() => { setEdit(false); setText(p.text); }} className="ghost-gold h-10 px-5 rounded-full">Cancel</button>
          </div>
        </>
      ) : <p className="text-lg text-[#E3DAF2] leading-relaxed" data-testid={`para-text-${p.id}`}>{p.text}{p.edited && <span className="ml-2 text-xs text-[#C9B6E4]">(corrected by you)</span>}</p>}
      <div className="flex flex-wrap gap-2 mt-2 text-sm">
        <button onClick={() => setShow(!show)} data-testid={`para-sources-${p.id}`} className="text-[#B57BFF] underline underline-offset-4">
          Source: {p.source_ids.map((i) => sources[i] ? `${sources[i].speaker} → ${sources[i].recipient}` : "recording").join(", ")}
        </button>
        {!edit && <button onClick={() => setEdit(true)} data-testid={`para-edit-${p.id}`} className="text-[#C9C2D8] flex items-center"><Pencil className="h-3.5 w-3.5 mr-1" />Correct</button>}
        {!edit && <button onClick={() => save({ delete: true })} data-testid={`para-delete-${p.id}`} className="text-[#C9C2D8] flex items-center"><Trash2 className="h-3.5 w-3.5 mr-1" />Remove</button>}
      </div>
      {show && p.source_ids.map((i) => sources[i] && (
        <div key={i} className="mt-2 rounded-lg bg-[#1A1222] border border-[#33264A] p-3" data-testid={`para-source-${p.id}-${i}`}>
          <SignedAudio controls path={sources[i].audio_path} className="w-full mb-2" />
          <p className="text-sm text-[#A49CB8] italic">“{sources[i].transcript}”</p>
        </div>
      ))}
    </div>
  );
}

function Clarification({ c, onSaved }) {
  const [answer, setAnswer] = useState("");
  const send = async (body) => { try { onSaved((await api.post(`/story2/draft/clarifications/${c.id}`, body)).data); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="rounded-xl border border-[rgba(139,44,255,0.4)] p-4 mb-3" data-testid={`clar-${c.id}`}>
      <p className="text-[#EDE6F7]">{c.question}</p>
      {c.why && <p className="text-sm text-[#A49CB8] mt-1">{c.why}</p>}
      {c.status === "open" ? (
        <div className="flex flex-col sm:flex-row gap-2 mt-3">
          <input value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="Your answer" data-testid={`clar-input-${c.id}`} className="flex-1 h-11 rounded-lg bg-[#1A1222] border border-[#33264A] px-3 text-[#EDE6F7]" />
          <button onClick={() => send({ answer })} data-testid={`clar-answer-${c.id}`} className="gold-btn h-11 px-5 rounded-full">Answer</button>
          <button onClick={() => send({ dismiss: true })} data-testid={`clar-dismiss-${c.id}`} className="ghost-gold h-11 px-5 rounded-full">Leave it out</button>
        </div>
      ) : <p className="text-sm text-[#D9C6FF] mt-2">{c.status === "answered" ? `Answered: ${c.answer} — create a new draft to use it.` : "Left out of the story."}</p>}
    </div>
  );
}

export function StoryDraft({ data, setData, treeConfirmed, job, onGenerate }) {
  const [ok, setOk] = useState(false);
  const d = data.draft;
  const sources = Object.fromEntries(data.sources.map((s) => [s.id, s]));
  const approve = async () => { try { setData((await api.post("/story2/draft/approve", { confirm: ok })).data); toast.success("Story approved."); } catch (e) { toast.error(apiError(e)); } };
  if (job?.status === "processing") return <div className="glass-card p-8 text-center" role="status" data-testid="draft-progress"><Loader2 className="h-8 w-8 animate-spin mx-auto text-[#B57BFF] mb-3" />Writing your draft from the recordings…</div>;
  return (
    <section data-testid="story-draft">
      {job?.status === "failed" && <p className="mb-4 flex gap-2 text-[#E6CCF2]" role="alert" data-testid="draft-error"><AlertCircle className="h-5 w-5" />{job.error}</p>}
      {!treeConfirmed && <p className="mb-4 text-[#D9C6FF]" data-testid="draft-needs-tree">Please confirm your family tree first.</p>}
      <button onClick={onGenerate} disabled={!treeConfirmed} data-testid="draft-generate-btn" className="gold-btn h-12 px-6 rounded-full mb-6 disabled:opacity-50">
        {d?.sections ? "Write a new draft (replaces your corrections)" : "Write the story draft"}
      </button>
      {d?.sections && (
        <>
          <h2 className="font-serif text-3xl text-[#EDE6F7]" data-testid="draft-title">{d.title}</h2>
          <p className="text-[#A49CB8] mb-2" data-testid="draft-length">About {d.minutes} minutes when narrated · {d.words} words, from {d.source_words} words of recordings</p>
          {d.minutes < 10 && <p className="text-sm text-[#8C84A0] mb-4">The story is shorter than 10 minutes because it only uses what was actually said.</p>}
          {d.checks?.length > 0 && <p className="text-sm text-[#C9B6E4] mb-4" data-testid="draft-checks">Automatic checks: {d.checks.join(" ")}</p>}
          {d.clarifications?.length > 0 && <div className="mb-6"><p className="eyebrow mb-2">Questions before approval</p>{d.clarifications.map((c) => <Clarification key={c.id} c={c} onSaved={setData} />)}</div>}
          {d.sections.map((s) => (
            <article key={s.id} className="glass-card p-6 mb-4" data-testid={`section-${s.id}`}>
              <p className="eyebrow mb-1" data-testid={`section-speaker-${s.id}`}>Told by {s.speaker}</p>
              <h3 className="font-serif text-2xl text-[#EDE6F7] mb-4">{s.heading}</h3>
              {s.paragraphs.map((p) => <Paragraph key={p.id} p={p} sources={sources} onSaved={setData} />)}
            </article>
          ))}
          {d.status === "approved" ? (
            <p className="flex gap-2 text-[#D9C6FF] text-lg" data-testid="draft-approved"><CheckCircle2 className="h-6 w-6" />Approved on {new Date(d.approved_at).toLocaleString("en-GB")}. Any further correction will need approving again.</p>
          ) : (
            <div className="glass-card p-6">
              {d.invalidated_reason && <p className="text-sm text-[#E6CCF2] mb-3" data-testid="draft-invalidated">Approval was cleared because the {d.invalidated_reason}.</p>}
              <label className="flex gap-3 items-start text-[#C9C2D8]"><input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} className="mt-1 h-5 w-5 accent-[#8B2CFF]" data-testid="draft-approve-checkbox" />
                I've read the whole story. Every memory is attributed to the right person and nothing has been added that wasn't said.</label>
              <button onClick={approve} disabled={!ok} data-testid="draft-approve-btn" className="gold-btn h-12 px-6 rounded-full mt-4 disabled:opacity-50">Approve this story</button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
